import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LOGIN, REGISTER, AUTH_EXTRA } from "@/constants/testIds";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { ArrowRight, Grid3x3 } from "lucide-react";

export default function Login() {
  const { t, i18n } = useTranslation();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
        toast.success(t("login_success"));
      } else {
        await register(email.trim(), password, name.trim() || undefined);
        toast.success(t("register_success"));
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const switchLang = () => {
    const next = i18n.language === "zh" ? "en" : "zh";
    i18n.changeLanguage(next);
    localStorage.setItem("lang", next);
  };

  return (
    <div className="h-screen w-screen flex bg-background text-foreground">
      {/* Left panel — hero */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 relative overflow-hidden border-r border-border/60 grain"
           style={{
             backgroundImage:
               "url(https://images.unsplash.com/photo-1532456745301-b2c645d8b80d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYWJzdHJhY3QlMjBncmlkJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3ODM5MDEyOTB8MA&ixlib=rb-4.1.0&q=85)",
             backgroundSize: "cover",
             backgroundPosition: "center",
           }}>
        <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px]" />
        <div className="relative p-10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md border-2 border-[hsl(var(--brand))] flex items-center justify-center bg-white shadow-[0_2px_8px_-2px_hsl(var(--brand)/0.35)]">
            <Grid3x3 className="w-4 h-4 text-[hsl(var(--brand))]" strokeWidth={2} />
          </div>
          <div>
            <div className="font-display font-bold text-lg tracking-tight">{t("app_name")}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-widest">{t("app_tagline")}</div>
          </div>
        </div>
        <div className="relative p-10 space-y-4">
          <div className="font-mono text-xs text-muted-foreground">// FEATURES</div>
          <ul className="space-y-2 font-display text-2xl leading-snug tracking-tight max-w-md">
            <li>— {i18n.language === "zh" ? "1000+ 报价单团队共享" : "Share 1000+ quotations across your team"}</li>
            <li>— {i18n.language === "zh" ? "单元格实时自动保存" : "Cell-level auto-save"}</li>
            <li>— {i18n.language === "zh" ? "编辑锁定,冲突为零" : "Edit-lock to prevent conflicts"}</li>
            <li>— {i18n.language === "zh" ? "一键导出 .xlsx" : "One-click .xlsx export"}</li>
          </ul>
        </div>
        <div className="relative p-10 flex items-center justify-between font-mono text-[11px] tracking-wider text-muted-foreground uppercase">
          <span>Est. 2026 · Team Edition</span>
          <span>v1.0</span>
        </div>
      </div>

      {/* Right panel — form */}
      <div className="flex-1 flex flex-col">
        <div className="flex items-center justify-end p-6 gap-2">
          <button
            type="button"
            onClick={switchLang}
            data-testid="auth-lang-toggle"
            className="btn-swiss text-xs h-8 px-3"
          >
            {i18n.language === "zh" ? "EN" : "中文"}
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 pb-10">
          <div className="w-full max-w-sm">
            <div className="mb-8">
              <div className="font-mono text-[11px] tracking-widest text-muted-foreground uppercase mb-2">
                {mode === "login" ? "// SIGN IN" : "// CREATE ACCOUNT"}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                {mode === "login" ? t("login") : t("register")}
              </h1>
              <p className="text-sm text-muted-foreground mt-3">
                {mode === "login" ? t("app_tagline") : t("register_cta")}
              </p>
            </div>

            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <div>
                  <label className="block text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                    {t("display_name")} <span className="text-muted-foreground/60">({t("name_optional")})</span>
                  </label>
                  <input
                    data-testid={AUTH_EXTRA.nameInput}
                    className="w-full h-11 px-3 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand)/0.3)] focus:border-[hsl(var(--brand))] tx-bg"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    autoComplete="name"
                  />
                </div>
              )}
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                  {t("email")}
                </label>
                <input
                  data-testid={mode === "login" ? LOGIN.emailInput : REGISTER.emailInput}
                  type="email"
                  required
                  className="w-full h-11 px-3 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand)/0.3)] focus:border-[hsl(var(--brand))] tx-bg"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <div>
                <label className="block text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2">
                  {t("password")}
                </label>
                <input
                  data-testid={mode === "login" ? LOGIN.passwordInput : REGISTER.passwordInput}
                  type="password"
                  required
                  minLength={6}
                  className="w-full h-11 px-3 border border-border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand)/0.3)] focus:border-[hsl(var(--brand))] tx-bg"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                />
              </div>

              {error && (
                <div data-testid={AUTH_EXTRA.errorMsg} className="text-sm text-destructive font-mono border border-destructive/40 bg-destructive/5 px-3 py-2 rounded-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                data-testid={mode === "login" ? LOGIN.submitButton : REGISTER.submitButton}
                className="btn-swiss primary w-full h-12 text-base"
              >
                {loading ? "…" : mode === "login" ? t("login_cta") : t("register_cta")}
                <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
              </button>
            </form>

            <div className="mt-6 text-sm text-muted-foreground flex items-center gap-2">
              {mode === "login" ? t("no_account") : t("have_account")}
              <button
                type="button"
                data-testid={AUTH_EXTRA.toggleModeBtn}
                onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
                className="text-primary font-semibold hover:underline tx-bg"
              >
                {mode === "login" ? t("switch_register") : t("switch_login")}
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 font-mono text-[11px] text-muted-foreground uppercase tracking-widest border-t border-border/60">
          © 2026 · QuoteDesk · Internal Tool
        </div>
      </div>
    </div>
  );
}
